import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import CardFrame from './atoms/CardFrame';
import TopSellersRankingSkeleton from './TopSellersRankingSkeleton';
import { RootState, useGetEventSalesByUserQuery } from '../store';

interface Props {
  businessId: string;
  eventId: string;
  title?: boolean
}

const TopSellersRanking: React.FC<Props> = ({ businessId, eventId, title = true }) => {
  const { user } = useSelector((state: RootState) => state.auth);

  const { data, isFetching } = useGetEventSalesByUserQuery(
    { businessId, eventId },
    { skip: !businessId || !eventId },
  );

  const topTen = useMemo(() => {
    if (!data) {
      return [];
    }
    const sorted = [...data].sort((a, b) => (b.items_sold || 0) - (a.items_sold || 0));
    return sorted.slice(0, 10);
  }, [data]);

  if (isFetching) {
    return <TopSellersRankingSkeleton />;
  }

  if (!topTen.length) {
    return (
      <CardFrame className="w-full">
        <div className="flex flex-col gap-2">
          {title && <h3 className="text-lg-medium text-text-primary">Top vendedores</h3>}
          <p className="text-text-secondary">No hay datos disponibles</p>
        </div>
      </CardFrame>
    );
  }

  const getItemWrapperClass = (index: number): string => {
    if (index === 0) {
      return 'border-x-4 border-y border-primary-500';
    }
    if (index === 1) {
      return 'border-x-4 border-y border-secondary-500';
    }
    if (index === 2) {
      return 'border-x-4 border-y border-info-400';
    }
    return 'border-x-4 border-y border-border-tertiary';
  };

  const getRankBadgeClass = (index: number): string => {
    if (index === 0) {
      return 'text-primary-600 ';
    }
    if (index === 1) {
      return 'text-secondary-600';
    }
    if (index === 2) {
      return 'text-info-600';
    }
    return 'text-text-tertiary';
  };

  return (
    <CardFrame className="w-full">
      <div className="flex flex-col gap-3">
        {title && <h3 className="text-lg-medium text-text-primary">Top vendedores</h3>}
        <ul className="flex flex-col gap-2">
          {topTen.map((seller, index) => {
            const isCurrentUser = user?.id === seller.user_id;
            return (
              <li
                key={seller.user_id}
                className={`flex items-center justify-between rounded-lg px-3 py-2 ${getItemWrapperClass(index)}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`text-sm ${getRankBadgeClass(index)} shrink-0`}>#{index + 1}</span>
                  <span
                    className={`truncate text-text-primary ${isCurrentUser ? 'font-bold' : ''}`}
                    title={seller.username}
                  >
                    {seller.username || 'Sin nombre'}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-text-primary text-sm">Ventas:</span>
                  <span className={`text-text-primary ${index === 0 ? 'text-base-medium' : 'text-sm-medium'}`}>{seller.items_sold}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </CardFrame>
  );
};

export default TopSellersRanking;
